import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Loader2, FileText, Banknote, CalendarCheck, HardHat, Receipt, AlertOctagon,
  ArrowUpRight, Plus, Activity, ChevronRight,
} from "lucide-react";
import api from "../lib/api";
import { PageHeader, Section } from "../components/PageHeader";

const fmtAud = (n) => "$" + Number(n || 0).toLocaleString("en-AU", { minimumFractionDigits: 0, maximumFractionDigits: 0 });

export default function Dashboard() {
  const [board, setBoard] = useState(null);
  const [signals, setSignals] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: b }, { data: s }] = await Promise.all([
          api.get("/api/pipeline/board"),
          api.get("/api/signals/summary"),
        ]);
        setBoard(b);
        setSignals(s);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="p-10" data-testid="dashboard-loading">
        <Loader2 size={16} className="inline animate-spin mr-2" />
        <span className="text-[12px]" style={{ color: "var(--ink-muted)" }}>Loading…</span>
      </div>
    );
  }
  const cardsByCol = board?.cards_by_column || {};
  const allCards = Object.values(cardsByCol).flat();
  const byStage = (stage) => allCards.filter((c) => c.stage === stage);

  // Action-oriented counts
  const quotesAwaitingDecision = [...byStage("quote_sent"), ...byStage("quote_follow_up")];
  const depositsOutstanding = byStage("deposit_required");
  const readyToSchedule = [...byStage("deposit_received"), ...byStage("ready_to_schedule")];
  const inProgress = [...byStage("in_progress"), ...byStage("scheduled")];
  const invoicesOutstanding = allCards.filter((c) =>
    (c.stage === "final_invoice") && (c.paid_total < c.invoiced_total));
  const criticalBlockers = signals?.critical || 0;

  const totalPipelineValue = allCards.reduce((s, c) =>
    s + (["quote_draft","quote_sent","quote_follow_up","quote_approved",
          "deposit_required","deposit_received","ready_to_schedule","scheduled",
          "in_progress","practical_completion","final_invoice"].includes(c.stage)
         ? (c.quote_total || 0) : 0), 0);

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        eyebrow="Today"
        title="What needs attention"
        subtitle="The commercial lifecycle in real time — what's stalled, what's ready, what's owed."
        actions={
          <>
            <Link to="/pipeline" className="btn btn-ghost btn-sm" data-testid="dashboard-pipeline-link">
              <Activity size={13} /> Open pipeline
            </Link>
            <Link to="/leads?new=1" className="btn btn-primary btn-sm" data-testid="new-lead-cta">
              <Plus size={13} /> New enquiry
            </Link>
          </>
        }
      />

      <div className="px-10 pt-7 pb-12 fade-up space-y-5">
        {/* TOP — Action cards (the heart of the dashboard) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="action-cards">
          <ActionCard
            label="Quotes awaiting decision"
            count={quotesAwaitingDecision.length}
            sub="Sent or in follow-up — chase up"
            accent="var(--accent)"
            link="/pipeline"
            testId="action-quotes-awaiting"
            icon={FileText}
            value={fmtAud(quotesAwaitingDecision.reduce((s, c) => s + (c.quote_total || 0), 0))}
          />
          <ActionCard
            label="Deposits outstanding"
            count={depositsOutstanding.length}
            sub="Approved quotes without a deposit"
            accent="var(--warn)"
            link="/pipeline"
            testId="action-deposits-outstanding"
            icon={Banknote}
          />
          <ActionCard
            label="Jobs ready to schedule"
            count={readyToSchedule.length}
            sub="Deposit received, awaiting calendar"
            accent="var(--good)"
            link="/schedule"
            testId="action-ready-to-schedule"
            icon={CalendarCheck}
          />
          <ActionCard
            label="Jobs in progress"
            count={inProgress.length}
            sub="Scheduled or actively on the tools"
            accent="var(--accent)"
            link="/today"
            testId="action-jobs-active"
            icon={HardHat}
          />
          <ActionCard
            label="Invoices outstanding"
            count={invoicesOutstanding.length}
            sub="Final invoice raised, payment not yet recorded"
            accent="var(--warn)"
            link="/pipeline"
            testId="action-invoices-outstanding"
            icon={Receipt}
            value={fmtAud(invoicesOutstanding.reduce((s, c) => s + Math.max(0, (c.invoiced_total||0)-(c.paid_total||0)), 0))}
          />
          <ActionCard
            label="Critical blockers"
            count={criticalBlockers}
            sub="Active blocking signals"
            accent={criticalBlockers > 0 ? "var(--bad)" : "var(--good)"}
            link="/signals?severity=critical"
            testId="action-critical-blockers"
            icon={AlertOctagon}
          />
        </div>

        {/* Jobs summary strip */}
        <Section title="Jobs at a glance" action={
          <Link to="/pipeline" className="btn btn-ghost btn-sm" data-testid="see-pipeline">
            View board <ArrowUpRight size={12} />
          </Link>
        }>
          <div className="surface-1 px-5 py-4" data-testid="pipeline-summary-strip">
            <div className="flex items-baseline justify-between mb-3">
              <div className="text-[11px] font-extrabold uppercase tracking-[0.14em]" style={{ color: "var(--ink-muted)" }}>
                Total in flight
              </div>
              <div className="font-mono text-[22px] font-extrabold" style={{ color: "var(--accent)" }}>
                {fmtAud(totalPipelineValue)}
              </div>
            </div>
            <div className="grid grid-cols-4 sm:grid-cols-8 gap-1">
              {(board?.columns || []).map((c) => (
                <Link
                  key={c.key} to="/pipeline"
                  className="block px-2 py-2 hover:bg-[var(--bg-elev-2)]"
                  style={{ background: "var(--bg-elev-2)", borderRadius: 4 }}
                  data-testid={`column-summary-${c.key}`}
                >
                  <div className="text-[9.5px] font-extrabold uppercase tracking-[0.12em] truncate"
                       style={{ color: "var(--ink-muted)" }}>{c.label}</div>
                  <div className="font-mono text-[15px] font-extrabold mt-0.5"
                       style={{ color: cardsByCol[c.key]?.length ? "var(--ink)" : "var(--ink-faint)" }}>
                    {cardsByCol[c.key]?.length || 0}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </Section>
      </div>
    </div>
  );
}

function ActionCard({ label, count, sub, accent, link, testId, icon: Icon, value }) {
  return (
    <Link to={link} data-testid={testId}
          className="surface-1 px-4 py-3.5 block hover:bg-[var(--bg-elev-2)] transition-colors"
          style={{ borderLeft: `3px solid ${accent}` }}>
      <div className="flex items-start justify-between gap-2 mb-1">
        <div className="flex items-center gap-2 min-w-0">
          {Icon && <Icon size={14} style={{ color: accent, flexShrink: 0 }} />}
          <span className="text-[10.5px] font-extrabold uppercase tracking-[0.16em] truncate"
                style={{ color: "var(--ink-muted)" }}>{label}</span>
        </div>
        <ChevronRight size={12} style={{ color: "var(--ink-faint)" }} />
      </div>
      <div className="flex items-baseline justify-between">
        <div className="font-mono text-[26px] font-extrabold leading-none"
             style={{ color: count > 0 ? accent : "var(--ink-muted)" }}
             data-testid={`${testId}-count`}>
          {count}
        </div>
        {value && (
          <div className="font-mono text-[12.5px]" style={{ color: "var(--ink-soft)" }}>{value}</div>
        )}
      </div>
      <div className="text-[10.5px] mt-1.5" style={{ color: "var(--ink-muted)" }}>{sub}</div>
    </Link>
  );
}
